/**
 * "Zo krijgt de fan het" — één voorvertoning voor alles wat uitgaat.
 *
 * 18-09-2026 (Jordy). Twee opdrachten kwamen hier samen: de verplichte preview
 * bij Mass Messages (HERSTEL-008 punt 6) en de vraag om vóór het verzenden te
 * kunnen zien én afspelen wat je stuurt. Dat is één ding, dus het staat op één
 * plek: de bijlagenkiezer en het Mass DM-venster gebruiken allebei dit
 * component.
 *
 * Wat het toont: de bijlagen zoals ze aankomen (een video is een speler, geen
 * plaatje), het bijschrift, en of het gratis of betaald gaat. Wat het NIET
 * doet: iets versturen. Dit is puur kijken.
 */
import { Modal } from './Modal'
import { credits } from '../lib/format'

export interface PreviewItem {
  id: number
  titel: string
  /** 'photo' | 'video' | 'audio' — bepaalt of het een speler wordt. */
  soort: string
  /** De bron; bij media is dit het echte bestand, zodat afspelen ook echt kan. */
  url: string
}

/** De prijsregel zoals de fan hem ziet: gratis of een bedrag in de juiste eenheid. */
export function prijsRegel(betaald: boolean, prijs: string, f2f: boolean): string {
  if (!betaald) return 'Free — the fan can open this right away.'
  const bedrag = String(prijs || '').trim()
  if (!bedrag) return 'Paid — no price filled in yet.'
  return f2f
    ? `Paid — the fan pays €${bedrag} to unlock this.`
    // 02-10-2026 (Jordy): FanCentro in credits met groepering, "4,000 credits".
    : `Paid — the fan pays ${credits(bedrag)} to unlock this.`
}

/** Eén bijlage in de voorvertoning. Video en geluid zijn echte spelers. */
export function PreviewMedia({ item }: { item: PreviewItem }) {
  const soort = String(item.soort || '').toLowerCase()
  if (soort === 'video') {
    return <video className="previewmedia" src={item.url} controls preload="metadata" />
  }
  if (soort === 'audio') {
    return <audio src={item.url} controls style={{ width: '100%' }} />
  }
  return <img className="previewmedia" src={item.url} alt={item.titel} />
}

export function VerzendPreview({
  items,
  bijschrift,
  betaald,
  prijs,
  f2f,
  extra,
  onSluit,
  onBevestig,
  bevestigTekst = 'Send',
}: {
  items: PreviewItem[]
  bijschrift: string
  betaald?: boolean
  prijs?: string
  f2f?: boolean
  /** Extra regels boven de bubbel, bijvoorbeeld het bereik van een mass DM. */
  extra?: React.ReactNode
  onSluit: () => void
  /** Laat je dit weg, dan is het puur kijken (geen verzendknop). */
  onBevestig?: () => void
  bevestigTekst?: string
}) {
  return (
    <Modal
      title={PREVIEW_TITEL}
      desc="Nothing is sent from this screen. Play the video here to check it before it goes out."
      width={620}
      onClose={onSluit}
      footer={
        <>
          <button className="btn ghost sm" type="button" onClick={onSluit}>
            {onBevestig ? 'Back' : 'Close'}
          </button>
          {onBevestig ? (
            <button className="btn primary sm" type="button" onClick={onBevestig}>
              {bevestigTekst}
            </button>
          ) : null}
        </>
      }
    >
      <VerzendPreviewInhoud items={items} bijschrift={bijschrift} betaald={betaald}
                            prijs={prijs} f2f={f2f} extra={extra} />
    </Modal>
  )
}

export const PREVIEW_TITEL = 'Preview — this is what the fan gets'

/**
 * De inhoud van de preview zonder eigen venster (01-10-2026). Het Mass
 * DM-venster toont hem IN het venster: een tweede venster erbovenop legde een
 * laag over de knoppen eronder, die daardoor zichtbaar bleven maar niets deden
 * (Jordy, 30-09: "Preview count doet niets").
 */
export function VerzendPreviewInhoud({
  items, bijschrift, betaald, prijs, f2f, extra,
}: {
  items: PreviewItem[]
  bijschrift: string
  betaald?: boolean
  prijs?: string
  f2f?: boolean
  extra?: React.ReactNode
}) {
  return (
    <>
      {extra ? <div className="field">{extra}</div> : null}
      <div className="fanbubbel">
        {items.length ? (
          <div className={items.length > 1 ? 'bundelmedia' : undefined}>
            {items.map((it) => <PreviewMedia key={it.id} item={it} />)}
          </div>
        ) : null}
        {bijschrift.trim() ? (
          <div className="btext">{bijschrift}</div>
        ) : items.length ? null : (
          <div className="mut2" style={{ fontSize: 12 }}>No message and no media yet.</div>
        )}
      </div>
      {items.length ? (
        <div className="mut2" style={{ fontSize: 12, marginTop: 8 }}>
          {items.length} item{items.length === 1 ? '' : 's'} ·{' '}
          {prijsRegel(Boolean(betaald), String(prijs ?? ''), Boolean(f2f))}
        </div>
      ) : null}
    </>
  )
}
